import { api, ApiFeil } from './klient';
import type { SvarResultat, Svarverdi } from './reise';

export interface Funn {
  rad: number | null;
  kolonne: string | null;
  verdi: string | null;
}

export interface Funngruppe {
  type: string;
  alvor: 'feil' | 'advarsel';
  antall: number;
  eksempler: Funn[];
}

export interface Forklaringsgruppe {
  type: string;
  antall: number;
  eksempler: string[];
  forklaring: string;
  slikRetterDu: string;
  kundenKanRette: boolean;
}

export interface Filforklaring {
  oppsummering: string;
  grupper: Forklaringsgruppe[];
}

export interface Kontroll {
  rader: number;
  feil: number;
  advarsler: number;
  grupper: Funngruppe[];
  forklaring: Filforklaring | null;
}

export interface Fil {
  id: string;
  filnavn: string;
  type: string;
  sporsmalId: string | null;
  storrelse: number;
  /** `mottatt` til kontrollen er ferdig, så `kontrollert` eller `feilet`. */
  status: 'mottatt' | 'kontrollert' | 'feilet';
  feilkode: string | null;
  opplastet: string;
  kontroll: Kontroll | null;
  /** Det kunden skrev om filen. */
  beskrivelse?: string | null;
  /** Dokumentleserens tolkning (T101), når den kunne brukes. */
  tolkning?: Tolkning | null;
}

export interface Dokumentstatistikk {
  rader: number;
  varer: number | null;
  lokasjoner: number | null;
  lokasjonsformat: string | null;
  ordre: number | null;
  dager: number | null;
  ordrePerDag: number | null;
  linjerPerDag: number | null;
  flestOrdrePaEnDag: number | null;
  fra: string | null;
  til: string | null;
  /** Prosenten av linjene de 20 % mest solgte varene står for (T106). */
  andelToppvarer?: number | null;
}

/** Et forslag til svar fra et dokument (T102). Ingenting er lagret før kunden bekrefter. */
export interface Forslag {
  filId: string;
  filnavn: string;
  sporsmalId: string;
  instans: string;
  modulTittel: string;
  sporsmalTekst: string;
  funn: string;
  verdi: Svarverdi;
  forslagTekst: string;
}

export interface Tolkning {
  dokumenttype: string;
  oppsummering: string;
  kolonner: { kolonne: string; betyr: string }[];
  funn: { tekst: string; sporsmalId: string | null; forslag: string | null }[];
  personopplysninger: boolean;
  statistikk: Dokumentstatistikk | null;
}

/** Dokumentasjonen kunden har lastet opp, nyeste først. */
export interface Dokumentasjon {
  filer: Fil[];
}

/** Filtypene som tas imot, for `accept` i filvelgeren. Det samme sjekker API-et. */
export const ENDELSER: Record<string, string> = {
  dokumentasjon: '.csv,.xlsx,.pdf,.png,.jpg,.jpeg',
  lagerfil: '.csv,.xlsx',
  salgshistorikk: '.csv,.xlsx',
  annet: '.csv,.xlsx,.pdf',
  bilde: '.jpg,.jpeg,.png',
};

/**
 * Laster opp filen. Med spørsmål er det svaret på et spørsmål av typen fil. Uten er det dokumentasjon, med en
 * valgfri beskrivelse. Regneark kontrolleres i bakgrunnen.
 */
async function lastOpp(fil: File, mal: { sporsmalId: string; instans: string } | { beskrivelse?: string }): Promise<Fil> {
  const skjema = new FormData();
  skjema.append('fil', fil, fil.name);
  if ('sporsmalId' in mal) {
    skjema.append('sporsmalId', mal.sporsmalId);
    skjema.append('instans', mal.instans);
  } else if (mal.beskrivelse?.trim()) {
    skjema.append('beskrivelse', mal.beskrivelse.trim());
  }

  let svar: Response;
  try {
    svar = await fetch('/api/reise/filer', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: skjema });
  } catch {
    throw new ApiFeil(0, 'nettverk');
  }

  if (!svar.ok) {
    let kode = 'ukjent';
    try {
      const problem = await svar.json();
      if (typeof problem?.kode === 'string') kode = problem.kode;
    } catch {
      // Svaret var ikke JSON, for eksempel når serveren selv stopper en for stor fil
      if (svar.status === 413) kode = 'for_stor_fil';
    }
    throw new ApiFeil(svar.status, kode);
  }

  return (await svar.json()) as Fil;
}

export const filApi = {
  dokumentasjon: () => api<Dokumentasjon>('/api/reise/dokumentasjon'),
  forslag: () => api<Forslag[]>('/api/reise/forslag'),
  bekreftForslag: (f: Forslag) =>
    api<SvarResultat>('/api/reise/forslag/bekreft', 'POST', { filId: f.filId, sporsmalId: f.sporsmalId, instans: f.instans }),
  avvisForslag: (f: Forslag) => api<void>('/api/reise/forslag/avvis', 'POST', { filId: f.filId, sporsmalId: f.sporsmalId, instans: f.instans }),
  lastOpp,
  fil: (id: string) => api<Fil>(`/api/reise/filer/${encodeURIComponent(id)}`),
};
